/**
 * useAnnouncements.ts — React Query hooks for Announcements.
 *
 * Vendor side: list mine, create, publish, withdraw.
 * Distributor side: feed, convert-to-handshake.
 */
import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryOptions,
} from "@tanstack/react-query";
import {
  announcementService,
  type Announcement,
  type AnnouncementKind,
  type AnnouncementStatus,
  type CreateAnnouncementInput,
  type ConvertToHandshakeInput,
} from "../services/announcementService";
import type { Handshake } from "../services/handshakeService";
import { handshakeKeys } from "./useHandshakes";

export const announcementKeys = {
  all: ["announcements"] as const,
  mine: () => [...announcementKeys.all, "mine"] as const,
  mineFiltered: (status?: AnnouncementStatus) =>
    [...announcementKeys.mine(), { status: status || "all" }] as const,
  feed: () => [...announcementKeys.all, "feed"] as const,
  feedFiltered: (kind?: AnnouncementKind) =>
    [...announcementKeys.feed(), { kind: kind || "all" }] as const,
  details: () => [...announcementKeys.all, "detail"] as const,
  detail: (id: string) => [...announcementKeys.details(), id] as const,
};

// ─── Queries ───────────────────────────────────────────────────────

export function useMyAnnouncements(
  status?: AnnouncementStatus,
  options?: Omit<UseQueryOptions<Announcement[]>, "queryKey" | "queryFn">,
) {
  return useQuery({
    queryKey: announcementKeys.mineFiltered(status),
    queryFn: () => announcementService.listMine(status),
    staleTime: 30_000,
    ...options,
  });
}

export function useAnnouncementFeed(
  kind?: AnnouncementKind,
  options?: Omit<UseQueryOptions<Announcement[]>, "queryKey" | "queryFn">,
) {
  return useQuery({
    queryKey: announcementKeys.feedFiltered(kind),
    queryFn: () => announcementService.feed(kind),
    staleTime: 30_000,
    ...options,
  });
}

export function useAnnouncement(
  id: string | null,
  options?: Omit<UseQueryOptions<Announcement>, "queryKey" | "queryFn">,
) {
  return useQuery({
    queryKey: announcementKeys.detail(id || ""),
    queryFn: () => announcementService.get(id!),
    enabled: !!id,
    staleTime: 15_000,
    ...options,
  });
}

// ─── Mutations ─────────────────────────────────────────────────────

export function useCreateAnnouncement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateAnnouncementInput) =>
      announcementService.create(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: announcementKeys.mine() });
    },
  });
}

export function usePublishAnnouncement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, expiresAt }: { id: string; expiresAt?: string }) =>
      announcementService.publish(id, expiresAt),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: announcementKeys.mine() });
      qc.invalidateQueries({ queryKey: announcementKeys.feed() });
      qc.setQueryData(announcementKeys.detail(data._id), data);
    },
  });
}

export function useWithdrawAnnouncement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      announcementService.withdraw(id, reason),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: announcementKeys.mine() });
      qc.invalidateQueries({ queryKey: announcementKeys.feed() });
      qc.setQueryData(announcementKeys.detail(data._id), data);
    },
  });
}

export function useConvertToHandshake() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      input,
    }: {
      id: string;
      input: ConvertToHandshakeInput;
    }) =>
      announcementService.convertToHandshake(id, input) as Promise<Handshake>,
    onSuccess: (handshake) => {
      // Invalidate handshake list (new handshake was just created)
      qc.invalidateQueries({ queryKey: handshakeKeys.lists() });
      qc.setQueryData(handshakeKeys.detail(handshake._id), handshake);
      // Conversion count on the announcement increased
      qc.invalidateQueries({ queryKey: announcementKeys.feed() });
    },
  });
}
